import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  InputAdornment, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button,
} from "@mui/material";
import { StyledWelcomeBanner, StyledWrapper } from "../../dashboard/style";
import {
  StyledBoxStyle, StyledCustomerSearch, StyledOrdersContainer, StyledTableLayout,
} from "./style";
import { StyledSearchIcon, StyledTypographyText } from "../../virtual-locker/style";
import RoleTable from "../../../components/common/table/RoleTable";
import { ROLE_CREATE, ROLE_LIST, ROLE_UPDATE } from "../../../actions/admin/AdminActions";

const RoleManagement = () => {
  const dispatch = useDispatch();
  const { roleList } = useSelector((state) => state?.roleData);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [roleName, setRoleName] = useState("");

  const fetchRoles = useCallback(() => {
    dispatch({ type: ROLE_LIST });
  }, [dispatch]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleOpenCreate = () => {
    setEditRow(null);
    setRoleName("");
    setModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditRow(row);
    setRoleName(row.name);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setRoleName("");
    setEditRow(null);
  };

  const handleSubmit = () => {
    if (!roleName.trim()) return;
    const actionType = editRow ? ROLE_UPDATE : ROLE_CREATE;
    const payload = editRow
      ? { id: editRow.id, name: roleName, onSuccess: () => { fetchRoles(); handleClose(); } }
      : { name: roleName, onSuccess: () => { fetchRoles(); handleClose(); } };
    dispatch({ type: actionType, payload });
  };

  const columns = ["NO", "ROLE NAME", "ACTIONS"];

  const filtered = (roleList || []).filter((r) =>
    r.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allRecords = filtered.map((item, index) => ({
    no: index + 1,
    id: item.id,
    name: item.name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    rawName: item.name,
  }));

  return (
    <StyledWrapper>
      <StyledWelcomeBanner />

      <StyledTableLayout>
        <StyledTypographyText>ROLE MANAGEMENT</StyledTypographyText>

        <StyledBoxStyle>
          <StyledCustomerSearch
            placeholder="Search by role name"
            variant="outlined"
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleOpenCreate} sx={{ whiteSpace: "nowrap" }}>
            + Add Role
          </Button>
        </StyledBoxStyle>

        <StyledOrdersContainer>
          <RoleTable columns={columns} rows={allRecords} onEdit={handleOpenEdit} recordList={fetchRoles} />
          <TablePagination
            component="div"
            count={allRecords.length}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </StyledOrdersContainer>
      </StyledTableLayout>

      <Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{editRow ? "Edit Role" : "Create Role"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editRow ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledWrapper>
  );
};

export default RoleManagement;
